/**
 * Delivery of Netlise-sent (and owner-shared) messages, the pure half: the
 * delivery tag copy (design "Delivery tag"), channel glyphs, timeline text
 * for SENT / DELIVERED / READ / FAILED events, which messages can be
 * retried, and the 14-day credit usage bars. No React, so it is unit-tested.
 */
import type { MessageRow } from '../../internals/repository/InvoiceRepository';
import { ago } from './format';

export type GlyphKey = 'WHATSAPP' | 'SMS' | 'EMAIL' | 'LINK';
export interface Glyph { key: GlyphKey; letter: string; bg: string; name: string }

/** Channel colours are fixed brand colours (README), the same in light and dark. */
export const GLYPHS: Record<GlyphKey, Glyph> = {
  WHATSAPP: { key: 'WHATSAPP', letter: 'W', bg: '#128C5A', name: 'WhatsApp' },
  SMS: { key: 'SMS', letter: 'S', bg: '#0C8FD4', name: 'SMS' },
  EMAIL: { key: 'EMAIL', letter: 'E', bg: '#4E45E4', name: 'email' },
  LINK: { key: 'LINK', letter: 'L', bg: '#6A6A76', name: 'link' },
};

const GLYPH_OF: Record<string, GlyphKey> = {
  WHATSAPP: 'WHATSAPP', MANUAL_WHATSAPP: 'WHATSAPP',
  SMS: 'SMS', MANUAL_SMS: 'SMS',
  EMAIL: 'EMAIL', MANUAL_EMAIL: 'EMAIL',
};

/** Netlise and manual channels both map onto the four glyphs; anything else is the link glyph. */
export const glyphOf = (channel?: string | null): Glyph => GLYPHS[GLYPH_OF[channel || ''] ?? 'LINK'];

export type DeliveryState = 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'SHARED' | 'VIEWED';
export type DeliveryTone = 'default' | 'danger' | 'brand';

const SHARED_OTHER: Record<string, string> = {
  COPY_LINK: 'Link copied',
  COPY_MESSAGE: 'Message copied',
  QR_DOWNLOAD: 'QR code downloaded',
};

/** "by SMS", "on WhatsApp", "by email" — WhatsApp reads "on". */
const via = (g: Glyph) => (g.key === 'WHATSAPP' ? `on ${g.name}` : `by ${g.name}`);

/** The tag label for one delivery state on a channel. `at` is only used for VIEWED. */
export function deliveryLabel(state: DeliveryState, channel?: string | null, at?: number | null, now = Date.now()): string {
  const g = glyphOf(channel);
  switch (state) {
    case 'SENT': return `Sent by ${g.key === 'LINK' ? 'Netlise' : g.name}`;
    case 'DELIVERED': return 'Delivered';
    case 'READ': return g.key === 'LINK' ? 'Read' : `Read on ${g.name}`;
    case 'FAILED': return 'Not delivered';
    case 'SHARED':
      if (g.key === 'LINK') return SHARED_OTHER[channel || ''] ?? 'Shared';
      return `Shared ${via(g)}`;
    case 'VIEWED': {
      const when = ago(at ?? undefined, now);
      return when ? `Viewed ${when}` : 'Viewed';
    }
    default: return String(state);
  }
}

export const deliveryTone = (state: DeliveryState): DeliveryTone =>
  (state === 'FAILED' ? 'danger' : state === 'VIEWED' ? 'brand' : 'default');

export interface DeliveryTagView { key: string; glyph: Glyph; label: string; tone: DeliveryTone }

interface DeliveryLike {
  lastDelivery?: { channel?: string; state?: string; at?: number } | null;
  viewedAt?: number | null;
}

/** README: the last send-type tag plus the last viewed tag. */
export function deliveryTags(item: DeliveryLike, now = Date.now()): DeliveryTagView[] {
  const out: DeliveryTagView[] = [];
  const d = item.lastDelivery;
  if (d?.state) {
    const state = d.state as DeliveryState;
    out.push({ key: `d-${state}`, glyph: glyphOf(d.channel), label: deliveryLabel(state, d.channel, d.at, now), tone: deliveryTone(state) });
  }
  if (item.viewedAt) {
    out.push({ key: 'viewed', glyph: GLYPHS.LINK, label: deliveryLabel('VIEWED', null, item.viewedAt, now), tone: 'brand' });
  }
  return out;
}

// ── Message rows (Messages card, message log) ──────────────────────────────

export type MessageStatus = MessageRow['status'];

/** The delivery tag for a message row. */
export function messageTag(m: Pick<MessageRow, 'channel' | 'status'>): DeliveryTagView {
  const g = glyphOf(m.channel);
  const state: DeliveryState | null = m.status === 'BOUNCED' ? 'FAILED'
    : m.status === 'QUEUED' || m.status === 'SENDING' ? null
      : (m.status as DeliveryState);
  if (!state) return { key: m.status, glyph: g, label: m.status === 'QUEUED' ? 'Queued' : 'Sending', tone: 'default' };
  return { key: m.status, glyph: g, label: deliveryLabel(state, m.channel), tone: deliveryTone(state) };
}

/** Short status for the message log's Status column. */
export const STATUS_LABEL: Record<MessageStatus, string> = {
  QUEUED: 'Queued', SENDING: 'Sending', SENT: 'Sent', DELIVERED: 'Delivered', READ: 'Read', FAILED: 'Not delivered', BOUNCED: 'Not delivered',
};
export type StatusTone = 'muted' | 'pos' | 'neg';
export const statusTone = (s: MessageStatus): StatusTone =>
  (s === 'FAILED' || s === 'BOUNCED' ? 'neg' : s === 'READ' || s === 'DELIVERED' ? 'pos' : 'muted');

/** Only failed or bounced messages can be sent again (a new message, new credits). */
export const canRetry = (m: Pick<MessageRow, 'status'>) => m.status === 'FAILED' || m.status === 'BOUNCED';

/** A failed WhatsApp or SMS can still go out from the owner's own phone. */
export const offersDeviceShare = (m: Pick<MessageRow, 'status' | 'channel'>) =>
  canRetry(m) && (m.channel === 'WHATSAPP' || m.channel === 'SMS');

export const creditText = (n: number) => (n > 0 ? `${n} credit${n === 1 ? '' : 's'}` : 'Free');

export const TEMPLATE_LABEL: Record<string, string> = {
  invoice_issued: 'Invoice issued',
  invoice_reminder: 'Payment reminder',
  invoice_overdue: 'Overdue notice',
  payment_received: 'Payment received',
  quotation_sent: 'Quotation',
};
export const templateLabel = (key?: string | null) => (key ? TEMPLATE_LABEL[key] ?? key.replace(/_/g, ' ') : '—');

/** When a message happened, for display: the latest status time we have. */
export const messageTime = (m: Pick<MessageRow, 'createdAt' | 'sentAt' | 'readAt' | 'deliveredAt' | 'updatedAt'>) =>
  m.sentAt || m.createdAt;

/** Copy for a failed retry: 402 → not enough credits (the view offers device share). */
export function retryProblem(e: any): { text: string; noCredits: boolean } {
  if (e?.status === 402 || e?.code === 'insufficient_credits') return { text: 'Not enough credits', noCredits: true };
  if (e?.code === 'invalid_state') return { text: 'This message can no longer be retried. Reload to see its status.', noCredits: false };
  return { text: e?.message || 'Could not send it again. Try again.', noCredits: false };
}

// ── Timeline text ──────────────────────────────────────────────────────────

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Timeline copy for Netlise send events, or null for other types:
 * "Sent by WhatsApp to •••••••••567", "Delivered by SMS", "Read on WhatsApp",
 * "Email not delivered: bounced".
 */
export function deliveryEventText(e: { type: string; channel?: string | null; meta?: any }): string | null {
  const m: any = e.meta || {};
  const has = !!e.channel && GLYPH_OF[e.channel] !== undefined;
  const name = has ? glyphOf(e.channel).name : '';
  switch (e.type) {
    case 'SENT': return has ? `Sent by ${name}${m.to ? ` to ${m.to}` : ''}` : 'Sent';
    case 'DELIVERED': return has ? `Delivered by ${name}` : 'Delivered';
    case 'READ': return has ? `Read on ${name}` : 'Read';
    case 'FAILED': {
      const why = m.bounced ? 'bounced' : m.error ? String(m.error) : '';
      const head = has ? `${cap(name)} not delivered` : 'Not delivered';
      return why ? `${head}: ${why}` : head;
    }
    default: return null;
  }
}

/** Events drawn as a channel-coloured glyph square on the timeline. */
const CHANNEL_EVENTS = ['SENT', 'DELIVERED', 'READ', 'FAILED', 'SHARED_MANUAL'];
export const eventGlyph = (e: { type: string; channel?: string | null }): Glyph | null =>
  (CHANNEL_EVENTS.includes(e.type) && e.channel ? glyphOf(e.channel) : null);

// ── Usage ──────────────────────────────────────────────────────────────────

const COLOMBO_MS = 330 * 60 * 1000;
const dayOf = (ms: number) => new Date(ms + COLOMBO_MS).toISOString().slice(0, 10);

export interface UsageDay { date: string; credits: number }

/** Credits used per Colombo day for the last `days` days (oldest first, today last). */
export function usagePerDay(rows: Pick<MessageRow, 'createdAt' | 'creditCost'>[], now = Date.now(), days = 14): UsageDay[] {
  const out: UsageDay[] = [];
  const index = new Map<string, number>();
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = dayOf(now - i * 864e5);
    index.set(d, out.length);
    out.push({ date: d, credits: 0 });
  }
  rows.forEach((r) => {
    const i = index.get(dayOf(r.createdAt));
    if (i !== undefined) out[i].credits += Math.max(0, Number(r.creditCost) || 0);
  });
  return out;
}

/** The oldest moment `usagePerDay` looks at, to know when the log has been fetched far enough. */
export const usageWindowStart = (now = Date.now(), days = 14) => Date.parse(`${dayOf(now - (days - 1) * 864e5)}T00:00:00Z`) - COLOMBO_MS;
